import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { DatePicker } from 'antd';
import moment from 'moment';
import Swal from 'sweetalert2';
import { default as ReactSelect } from 'react-select';
import { Col, Row } from 'reactstrap';
import { HandleSelect, Option, ValueContainer, multiSelectSearchStyles } from './MultiSelectControls';
import {
    DASHBOARD_DATE_MODES,
    DASHBOARD_TAB_OPTIONS,
    getDefaultDashboardSettings,
    normalizeDisplayDate,
    sanitizeDashboardSettings,
} from '../utils/dashboardSettings';

function toPickerMoment(value) {
    if (!value) return null;
    if (moment.isMoment(value)) return value.isValid() ? value : null;
    const normalized = normalizeDisplayDate(value);
    if (!normalized) return null;
    const parsed = moment(normalized, 'DD-MM-YYYY', true);
    return parsed.isValid() ? parsed : null;
}

function buildPayload(settings, selectedPlant, allowedPlants) {
    const plantIds = getSelectedPlantIds(selectedPlant, allowedPlants);

    let defaultPlantId = settings.defaultPlantId;
    const pool = getEffectivePlantPool(selectedPlant, allowedPlants);
    const availableIds = pool.map((plant) => Number(plant.value));
    if (!availableIds.includes(Number(defaultPlantId))) {
        defaultPlantId = availableIds[0] ?? null;
    }

    return {
        ...settings,
        plantIds,
        defaultPlantId,
    };
}

function getSelectedPlantIds(selectedPlant, allowedPlants) {
    const pool = getEffectivePlantPool(selectedPlant, allowedPlants);
    if (!allowedPlants.length || pool.length === allowedPlants.length) {
        return [];
    }
    return pool.map((plant) => plant.value);
}

function getEffectivePlantPool(selectedPlant, allowedPlants) {
    if (!allowedPlants.length) {
        return [];
    }

    const hasSelectAll = selectedPlant.some((plant) => plant.value === 'select-all');
    const selectedIds = new Set(
        selectedPlant
            .filter((plant) => plant.value !== 'select-all')
            .map((plant) => Number(plant.value)),
    );

    if (
        hasSelectAll
        || selectedIds.size === 0
        || selectedIds.size >= allowedPlants.length
    ) {
        return allowedPlants;
    }

    return allowedPlants.filter((plant) => selectedIds.has(Number(plant.value)));
}

function plantIdsMatch(storedIds, plantValue) {
    return (storedIds || []).some((id) => Number(id) === Number(plantValue));
}

const datePickerPopupProps = {
    getCalendarContainer: () => document.body,
    dropdownClassName: 'sales-settings-date-popup',
};

const DashboardSettingsForm = forwardRef(function DashboardSettingsForm({
    allowedPlants = [],
    initialSettings,
    showSectionHeader = true,
}, ref) {
    const [settings, setSettings] = useState(() => (
        sanitizeDashboardSettings(initialSettings || getDefaultDashboardSettings())
    ));
    const [selectedPlant, setSelectedPlant] = useState([]);

    const plantsWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All plants' }, ...allowedPlants],
        [allowedPlants],
    );

    const defaultPlantOptions = useMemo(
        () => getEffectivePlantPool(selectedPlant, allowedPlants),
        [selectedPlant, allowedPlants],
    );

    const selectedDefaultPlant = useMemo(
        () => defaultPlantOptions.find(
            (plant) => Number(plant.value) === Number(settings.defaultPlantId),
        ) || defaultPlantOptions[0] || null,
        [defaultPlantOptions, settings.defaultPlantId],
    );

    const sharedSelectProps = {
        isMulti: true,
        isSearchable: true,
        controlShouldRenderValue: false,
        closeMenuOnSelect: false,
        hideSelectedOptions: false,
        openMenuOnFocus: true,
        openMenuOnClick: true,
        blurInputOnSelect: false,
        menuPlacement: 'auto',
        maxMenuHeight: 220,
        components: { Option, ValueContainer },
        styles: multiSelectSearchStyles,
        isClearable: true,
    };

    const getCurrentSettings = () => buildPayload(settings, selectedPlant, allowedPlants);

    useImperativeHandle(ref, () => ({
        getSettings: () => sanitizeDashboardSettings(getCurrentSettings()),
    }));

    useEffect(() => {
        setSettings(sanitizeDashboardSettings(initialSettings || getDefaultDashboardSettings()));
    }, [initialSettings]);

    useEffect(() => {
        const source = sanitizeDashboardSettings(initialSettings || getDefaultDashboardSettings());
        const plantSelection = source.plantIds?.length
            ? allowedPlants.filter((plant) => plantIdsMatch(source.plantIds, plant.value))
            : allowedPlants;
        setSelectedPlant(
            plantSelection.length === allowedPlants.length && allowedPlants.length
                ? plantsWithSelectAll
                : plantSelection,
        );
    }, [initialSettings, allowedPlants, plantsWithSelectAll]);

    useEffect(() => {
        if (!defaultPlantOptions.length) {
            return;
        }
        const currentId = Number(settings.defaultPlantId);
        const isValid = defaultPlantOptions.some(
            (plant) => Number(plant.value) === currentId,
        );
        if (!isValid) {
            setSettings((current) => ({
                ...current,
                defaultPlantId: defaultPlantOptions[0].value,
            }));
        }
    }, [defaultPlantOptions, settings.defaultPlantId]);

    const onPlantSelect = (selected) => {
        HandleSelect(selected, selectedPlant, setSelectedPlant, allowedPlants, plantsWithSelectAll);
        const nextPool = getEffectivePlantPool(selected || [], allowedPlants);
        if (!nextPool.length) {
            setSettings((current) => ({ ...current, defaultPlantId: null }));
            return;
        }
        const stillValid = nextPool.some(
            (plant) => Number(plant.value) === Number(settings.defaultPlantId),
        );
        if (!stillValid) {
            setSettings((current) => ({
                ...current,
                defaultPlantId: nextPool[0].value,
            }));
        }
    };

    const handleDateModeChange = (event) => {
        const dateMode = event.target.value;
        setSettings((current) => {
            if (dateMode !== DASHBOARD_DATE_MODES.DATE_RANGE) {
                return { ...current, dateMode };
            }
            const defaults = getDefaultDashboardSettings();
            return {
                ...current,
                dateMode,
                customFromDate: normalizeDisplayDate(current.customFromDate) || defaults.customFromDate,
                customToDate: normalizeDisplayDate(current.customToDate) || defaults.customToDate,
            };
        });
    };

    const handleFromDateChange = (date) => {
        const nextDate = toPickerMoment(date);
        if (!nextDate) return;
        setSettings((current) => ({
            ...current,
            dateMode: DASHBOARD_DATE_MODES.DATE_RANGE,
            customFromDate: nextDate.format('DD-MM-YYYY'),
        }));
    };

    const handleToDateChange = (date) => {
        const nextDate = toPickerMoment(date);
        if (!nextDate) return;
        setSettings((current) => ({
            ...current,
            dateMode: DASHBOARD_DATE_MODES.DATE_RANGE,
            customToDate: nextDate.format('DD-MM-YYYY'),
        }));
    };

    const toggleTab = (tabValue) => {
        setSettings((current) => {
            const enabled = new Set(current.enabledTabs || []);
            if (enabled.has(tabValue)) {
                if (enabled.size <= 1) {
                    Swal.fire('At least one tab required', '', 'warning');
                    return current;
                }
                enabled.delete(tabValue);
            } else {
                enabled.add(tabValue);
            }
            const enabledTabs = DASHBOARD_TAB_OPTIONS
                .map((tab) => tab.value)
                .filter((tab) => enabled.has(tab));
            const defaultTab = enabledTabs.includes(current.defaultTab)
                ? current.defaultTab
                : enabledTabs[0];
            return { ...current, enabledTabs, defaultTab };
        });
    };

    const defaults = getDefaultDashboardSettings();
    const customFrom = toPickerMoment(settings.customFromDate) || toPickerMoment(defaults.customFromDate);
    const customTo = toPickerMoment(settings.customToDate) || toPickerMoment(defaults.customToDate);

    return (
        <div className="sales-settings-section">
            {showSectionHeader ? (
                <>
                    <h5 className="sales-settings-section-title">General Settings for Dashboard</h5>
                    <p className="sales-settings-section-subtitle">
                        Configure default plants, date range, and dashboard tabs for the home screen.
                    </p>
                </>
            ) : null}

            <Row className="mb-3">
                <Col md={12}>
                    <label className="sales-settings-label">User allowed plants</label>
                    <ReactSelect
                        {...sharedSelectProps}
                        options={plantsWithSelectAll}
                        value={selectedPlant}
                        onChange={onPlantSelect}
                        className="react-select-dropdown"
                        placeholder="Please select Plant"
                    />
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={12}>
                    <label className="sales-settings-label">Default plant</label>
                    <ReactSelect
                        options={defaultPlantOptions}
                        value={selectedDefaultPlant}
                        onChange={(option) => setSettings((current) => ({
                            ...current,
                            defaultPlantId: option ? option.value : null,
                        }))}
                        className="react-select-dropdown"
                        placeholder="Select default plant"
                        isClearable={false}
                        isSearchable
                        menuPlacement="auto"
                        maxMenuHeight={220}
                        noOptionsMessage={() => 'No plants available — select plants above first'}
                    />
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={12}>
                    <label className="sales-settings-label">Default date range</label>
                    <div className="sales-settings-date-options">
                        <label className="sales-settings-radio">
                            <input
                                type="radio"
                                name="dashboardDateMode"
                                value={DASHBOARD_DATE_MODES.TODAY}
                                checked={settings.dateMode === DASHBOARD_DATE_MODES.TODAY}
                                onChange={handleDateModeChange}
                            />
                            Today
                        </label>
                        <label className="sales-settings-radio">
                            <input
                                type="radio"
                                name="dashboardDateMode"
                                value={DASHBOARD_DATE_MODES.CURRENT_MONTH}
                                checked={settings.dateMode === DASHBOARD_DATE_MODES.CURRENT_MONTH}
                                onChange={handleDateModeChange}
                            />
                            Current month (default)
                        </label>
                        <label className="sales-settings-radio">
                            <input
                                type="radio"
                                name="dashboardDateMode"
                                value={DASHBOARD_DATE_MODES.DATE_RANGE}
                                checked={settings.dateMode === DASHBOARD_DATE_MODES.DATE_RANGE}
                                onChange={handleDateModeChange}
                            />
                            Date range
                        </label>
                    </div>
                    {settings.dateMode === DASHBOARD_DATE_MODES.DATE_RANGE ? (
                        <div className="sales-settings-date-range">
                            <div className="sales-settings-date-field">
                                <label className="sales-settings-date-field-label">From date</label>
                                <DatePicker
                                    {...datePickerPopupProps}
                                    value={customFrom}
                                    format="DD-MM-YYYY"
                                    onChange={handleFromDateChange}
                                    allowClear={false}
                                    placement="bottomLeft"
                                    style={{ height: '40px', width: '100%' }}
                                />
                            </div>
                            <div className="sales-settings-date-field">
                                <label className="sales-settings-date-field-label">To date</label>
                                <DatePicker
                                    {...datePickerPopupProps}
                                    value={customTo}
                                    format="DD-MM-YYYY"
                                    onChange={handleToDateChange}
                                    allowClear={false}
                                    placement="topLeft"
                                    style={{ height: '40px', width: '100%' }}
                                />
                            </div>
                        </div>
                    ) : null}
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={12}>
                    <label className="sales-settings-label">Dashboard options</label>
                    <div className="sales-settings-date-options">
                        {DASHBOARD_TAB_OPTIONS.map((tab) => (
                            <label key={tab.value} className="sales-settings-radio">
                                <input
                                    type="checkbox"
                                    checked={(settings.enabledTabs || []).includes(tab.value)}
                                    onChange={() => toggleTab(tab.value)}
                                />
                                {tab.label}
                            </label>
                        ))}
                    </div>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={6}>
                    <label className="sales-settings-label">Default tab on load</label>
                    <select
                        className="form-control browser-default custom-select"
                        value={settings.defaultTab || 'schedule'}
                        onChange={(e) => setSettings((current) => ({
                            ...current,
                            defaultTab: e.target.value,
                        }))}
                    >
                        {DASHBOARD_TAB_OPTIONS
                            .filter((tab) => (settings.enabledTabs || []).includes(tab.value))
                            .map((tab) => (
                                <option key={tab.value} value={tab.value}>
                                    {tab.label}
                                </option>
                            ))}
                    </select>
                </Col>
                <Col md={6}>
                    <label className="sales-settings-label">Auto-refresh (every 20s)</label>
                    <div className="sales-settings-date-options" style={{ marginTop: 8 }}>
                        <label className="sales-settings-radio">
                            <input
                                type="checkbox"
                                checked={!!settings.autoRefresh}
                                onChange={(e) => setSettings((current) => ({
                                    ...current,
                                    autoRefresh: e.target.checked,
                                }))}
                            />
                            Enable live refresh when viewing today
                        </label>
                    </div>
                </Col>
            </Row>
        </div>
    );
});

export default DashboardSettingsForm;
